import React, { useCallback, useEffect, useState } from "react";
import { ReportModel } from "models/ReportModel";
import SelectControl from "components/Form/filterSelect/statusSelect";
import Tags from "components/Tags";
import Title from "components/Title/Title";
import "./reportsBase.scss";
import {
  AddExtraSelectOptions,
  convertToLabelValueObject,
} from "common/helpers/fitToSelectSrtcture";
import Loading from "components/Loading/Loading";
import { ReportStatus } from "models/ReportStatus";
import useFilterOptions from "components/MultiSelect/useFilterOptions";
import MultiFilterContainer from "components/MultiFilterContainer/MultiFilterContainer";
import {
  useAddOrUpdateSavedFilterMutation,
  useDeleteSavedFilterMutation,
  useGetSavedFiltersQuery,
} from "features/api/filters/filtersSlice";
import { useGetDecisionTypesListQuery } from "features/api/contentManagement/contentManagementApiSlice";
import { tDecisionType } from "i18n/enums";
import { useTranslation } from "react-i18next";
import { Funnel, XLg } from "react-bootstrap-icons";
import { expandedFiltersType, filtersType } from "pages/ReportsPage/types";
import RangeDatePicker from "common/components/DatePicker/RangePicker";
import { useRole } from "hooks/useRole";
import { useLocationForReportType } from "hooks/useLocationForReportType";
import { filterOption } from "models/filterOption";
import { getAdjustedISOString } from "pages/ControlCenter/ContentManagement/contentManagementUtils/contentManagementUtils";
import { useReports } from "hooks/useReports";
import CustomPagination from "common/components/Pagination/CustomPagination";
import ReportsLayout from "../ReportsLayout/ReportsLayout";
import {
  FilterType,
  getReportStatusLabel,
  PoliceDecisionArray,
  PoliceRejectionReasonArray,
  StatusesEnums,
  UserRole,
} from "../reportAssets/assets";
import MultiFilters from "./MultiFilters";
import ReportsList from "./reportsList";
import ReportsNotFound from "./reportsNotFound";

type ReportsBaseProps = {
  reports: ReportModel[];
  statuses: ReportStatus[];
  title: string;
  isLoading: boolean;
  isError: boolean;
  filters: filtersType;
  simpleFilters: filtersType;
  setFilters: React.Dispatch<filtersType>;
  expandedFilters: expandedFiltersType;
  setExpandedFilters: React.Dispatch<expandedFiltersType>;
  isFiltersRun: boolean;
  handleRunFilters: () => void;
  handleClearFilters: () => void;
  handlePageChange: React.Dispatch<number>;
  currentPage: number;
  totalReports: number;
  totalPages: number;
  checkedReportsIds: number[];
  setCheckedReportsIds: React.Dispatch<number[]>;
  onReportRefetch: () => void;
};

const ReportsBase = (props: ReportsBaseProps) => {
  const { t } = useTranslation();
  const {
    reports,
    statuses,
    title,
    isLoading,
    isError,
    filters,
    simpleFilters,
    setFilters,
    expandedFilters,
    setExpandedFilters,
    isFiltersRun,
    handleRunFilters,
    handleClearFilters,
    handlePageChange,
    currentPage,
    totalReports,
    totalPages,
    checkedReportsIds,
    setCheckedReportsIds,
    onReportRefetch,
  } = props;

  const {
    filtersOptions,
    filterOptionInit,
    setDataToFiltersOptions,
    runFilter,
    setRunFilter,
    setFiltersOptions,
    isOpenMultiSelect,
    isOpenSavedFilter,
    setIsOpenMultiSelect,
    setIsOpenSavedFilter,
    closeSavedFilterComponent,
    toggleOpenMultiSelect,
    toggleOpenSavedFilter,
    openMultiSelect,
    chosenFilter,
    setChosenFilter,
    checkForSavedData,
    filterOptionsObject,
  } = useFilterOptions(FilterType.reportType);

  const [reportDate, setReportDate] = useState();
  const [updateDate, setUpdateDate] = useState();

  const role = useRole();
  const isFollowUp = useLocationForReportType() === "followup";
  const { searchKeyword } = useReports();
  const { data: decisionTypes } = useGetDecisionTypesListQuery();

  const setDatesStates = (savedFilterDataForDates) => {
    if (savedFilterDataForDates.reportDate) {
      setReportDate((prevReportDate: any) => {
        const savedReportDate = checkForSavedData(
          "reportDate",
          savedFilterDataForDates
        );
        return savedReportDate || prevReportDate;
      });
    }
    if (savedFilterDataForDates.updateDate) {
      setUpdateDate((prevUpdateDate: any) => {
        const savedUpdateDate = checkForSavedData(
          "updateDate",
          savedFilterDataForDates
        );

        return savedUpdateDate || prevUpdateDate;
      });
    }
  };

  const setDatatoFilter = useCallback(() => {
    setDataToFiltersOptions(chosenFilter);
  }, [chosenFilter, setDataToFiltersOptions]);

  useEffect(() => {
    if (Object.keys(chosenFilter).length) {
      setDatatoFilter();
      setDatesStates(chosenFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenFilter]);

  // all statuses
  const statusFilters = statuses
    ? statuses
        .map((status) => {
          const enumValue = Object.values(StatusesEnums).find(
            (enumItem) => enumItem.id === status.id
          );
          return {
            // D-08: localized status DISPLAY label via the enum lookup helper
            // (matching/grouping below stays consistent with this label).
            label: enumValue
              ? getReportStatusLabel(enumValue.id, role)
              : status.type,
            value: status.id,
          };
        })
        .filter((filter) => filter.label !== null && filter.label !== undefined)
    : [];

  // select tags options
  const mergedFilters = statusFilters.reduce((accumulator, currentFilter) => {
    const existingFilter = accumulator.find(
      (filter) => filter.label === currentFilter.label
    );

    if (existingFilter) {
      existingFilter.value.push(currentFilter.value);
    } else {
      accumulator.push({
        label: currentFilter.label,
        value: [currentFilter.value],
      });
    }

    return accumulator;
  }, []);

  // tags list
  const mergeDuplicates = statuses.reduce((acc, status) => {
    const matchingEnum = Object.values(StatusesEnums).find(
      (enumItem) => enumItem.id === status.id
    );

    if (matchingEnum) {
      // D-08: dedup/group by the canonical Hebrew identity (text[role]) — stable
      // across language — but DISPLAY the localized label via the lookup helper.
      const localizedType = getReportStatusLabel(matchingEnum.id, role);
      const existingStatus = acc.find((s) => s.type === localizedType);

      if (existingStatus) {
        existingStatus.ids.push(status.id);
      } else {
        acc.push({
          type: localizedType,
          ids: [status.id],
        });
      }
    }

    return acc;
  }, []);

  mergedFilters.unshift({ label: t("כל הסטטוסים"), value: 999 });

  // Decision filter options now come from the server DecisionType table
  // (mapped to the {name, code} shape convertToLabelValueObject expects).
  // D-08: server provides the stable code (item.id); the DISPLAY label is
  // localized client-side via the i18n lookup (not the raw Hebrew DB name).
  let DecisionsFilters = convertToLabelValueObject(
    (decisionTypes || []).map((item) => ({
      name: tDecisionType(item.id),
      code: item.id,
    }))
  );
  DecisionsFilters = AddExtraSelectOptions(DecisionsFilters, "unshift");

  let policeDecisionsFilters = convertToLabelValueObject(PoliceDecisionArray);
  policeDecisionsFilters = AddExtraSelectOptions(
    policeDecisionsFilters,
    "unshift"
  );

  let policeRejectionReasonFilters = convertToLabelValueObject(
    PoliceRejectionReasonArray
  );
  policeRejectionReasonFilters = AddExtraSelectOptions(
    policeRejectionReasonFilters,
    "unshift"
  );

  const renderReportsNumber = () => {
    return totalReports;
  };

  const tagsSelectionChanged = (selectedTag: ReportStatus) => {
    const selectedTagIds = selectedTag.ids;

    let newTags = filters?.reportsStatuses ? [...filters.reportsStatuses] : [];

    selectedTagIds.forEach((tagId) => {
      if (newTags.includes(tagId)) {
        newTags = newTags.filter((tagItem) => tagItem !== tagId);
      } else {
        newTags.push(tagId);
      }
    });
    setFilters({
      ...filters,
      reportsStatuses: newTags.length > 0 ? newTags : null,
    });
  };

  const handleOnSelectOption = (
    filterName: string,
    selectedOption: {
      label: string;
      value: number | number[];
    }
  ) => {
    setFilters({
      ...filters,
      [filterName]: selectedOption.value !== 999 ? selectedOption.value : null,
    });
  };

  const renderList = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (isError) {
      return "Error";
    }

    return reports?.length ? (
      <ReportsList
        reports={reports}
        checkedReportsIds={checkedReportsIds}
        setCheckedReportsIds={setCheckedReportsIds}
        statuses={mergeDuplicates}
      />
    ) : (
      <ReportsNotFound />
    );
  };

  const onDateCahnge = (date, type: string) => {
    const dates: {
      startDate: Date;
      endDate: Date;
    } =
      type === "start"
        ? { startDate: date, endDate: filters?.reportUpdateDatesRange?.endDate }
        : {
            startDate: filters?.reportUpdateDatesRange?.startDate,
            endDate: date,
          };

    setFilters({
      ...filters,
      reportUpdateDatesRange: dates,
    });
  };

  const [addOrUpdateFilter, { isLoading: isSaving }] =
    useAddOrUpdateSavedFilterMutation();

  const {
    data: savedFilters,
    isLoading: isLoadingSavedFilters,
    isError: isErrorSavedFilters,
  } = useGetSavedFiltersQuery(role);

  const [deleteFilter, { isLoading: isDeleting }] =
    useDeleteSavedFilterMutation();

  const params = {
    title: {
      title,
      num: renderReportsNumber(),
    },
    tags: {
      tags: mergeDuplicates,
      onchange: tagsSelectionChanged,
      selectedTags: filters.reportsStatuses,
    },
    rangeDatePicker: {
      initialStartDate: null,
      initialEndDate: null,
      onStartDateChange: (date) => onDateCahnge(date, "start"),
      onEndDateChange: (date) => onDateCahnge(date, "end"),
      defaultStartDate: filters?.reportUpdateDatesRange
        ? filters?.reportUpdateDatesRange.startDate
        : new Date(),
      defaultEndDate: filters?.reportUpdateDatesRange
        ? filters?.reportUpdateDatesRange.endDate
        : new Date(),
      toResetDates: !isFiltersRun,
    },
    selectControl: {
      options: mergedFilters,
      onChange: handleOnSelectOption,
      label: t("status"),
      selectedOption: filters?.reportsStatuses
        ? mergedFilters.find((option) =>
            filters?.reportsStatuses?.every(
              (element, index) => element === option?.value[index]
            )
          )
        : { label: t("כל הסטטוסים"), value: 999 },
    },
    DecisionSelect: {
      options: DecisionsFilters,
      onChange: handleOnSelectOption,
      selectedOption: filters?.decisionId
        ? DecisionsFilters.find(
            (option) => filters?.decisionId === option?.value
          )
        : DecisionsFilters[0],
    },
    PoliceDecisionSelect: {
      options: policeDecisionsFilters,
      onChange: handleOnSelectOption,
      selectedOption: filters?.policeDecision
        ? policeDecisionsFilters.find(
            (option) => filters?.policeDecision === option?.value
          )
        : policeDecisionsFilters[0],
    },
    PoliceRejectionReason: {
      options: policeRejectionReasonFilters,
      onChange: handleOnSelectOption,
      selectedOption: filters?.policeRejectionReason
        ? policeRejectionReasonFilters.find(
            (option) => filters?.policeRejectionReason === option?.value
          )
        : policeRejectionReasonFilters[0],
    },
    multiSelect: {
      filterType: FilterType.reportType,
      reportDate,
      setReportDate,
      updateDate,
      setUpdateDate,
      filtersOptions,
      setFiltersOptions,
      filterOptionsObject,
      filterOptionInit,
      runFilter,
      setRunFilter,
      setChosenFilter,
      chosenFilter,
      dbRequest: {
        addOrUpdateFilter,
        isSaving,
      },
    },
    SavedFilters: {
      filterType: FilterType.reportType,
      setChosenFilter,
      chosenFilter,
      openTheMultiCollapseFormOutSide: openMultiSelect,
      closeSavedFilterComponent,
      setRunFilter,
      runFilter,
      setReportDate,
      setUpdateDate,
      filterOptionInit,
      dbRequest: {
        savedFilters,
        isLoading: isLoadingSavedFilters,
        isError: isErrorSavedFilters,
      },
      deleteDbRequest: {
        deleteFilter,
        isDeleting,
      },
    },
  };

  const renderStatusesFilter = () => {
    if (
      (isFollowUp && role === UserRole.SENIOR) ||
      role === UserRole.CONTROLCENTER ||
      role === UserRole.SENIOR_WATCH
    ) {
      return <SelectControl {...params.selectControl} />;
    }
    return <Tags {...params.tags} />;
  };

  const renderRestOfFilters = () => {
    return (
      <MultiFilters
        rangeDatePicker={params.rangeDatePicker}
        decisionSelect={params.DecisionSelect}
        policeDecisionSelect={params.PoliceDecisionSelect}
        policeRejectionReason={params.PoliceRejectionReason}
      />
    );
  };
  const renderClassName = () => {
    if (role === UserRole.CONTROLCENTER && !isFollowUp) {
      return "tagsAndSelectSeniorDisplay";
    }
    return "tagsAndSelect";
  };

  const renderFilterButton = () => {
    return (
      <div className="filterButtonsContainer">
        <button
          type="button"
          className="filterButton"
          onClick={handleRunFilters}
          disabled={
            role !== UserRole.REGULAR && isFollowUp
              ? Object.values(filters).every((value) => value === null)
              : false
          }
        >
          <div className={`dot ${isFiltersRun ? "active" : ""}`} />
          {t("filter")}
          <Funnel
            height={15}
            width={15}
            style={{ visibility: !isFiltersRun ? "visible" : "hidden" }}
          />
        </button>

        {isFiltersRun && (
          <button
            type="button"
            className="iconFilterButton"
            onClick={(e) => {
              e.stopPropagation();
              handleClearFilters();
            }}
            disabled={!isFiltersRun}
          >
            <XLg height={15} width={15} fill="red" />
          </button>
        )}
      </div>
    );
  };

  // here we submit the expandedfilters to the server from the selected filters and also from the saved filters
  const submitExpandedFilter = useCallback(
    (filtersToSendToServer: filterOption & expandedFiltersType) => {
      handlePageChange(1);

      if ("options" in filtersToSendToServer) {
        const formattedFilters = filtersToSendToServer.options
          .filter((filter) => filter.isSelected)
          .reduce((obj, option) => {
            const formattedObject = obj;
            if (
              option?.field === "reportDate" ||
              option?.field === "updateDate"
            ) {
              formattedObject[option.field] = getAdjustedISOString(
                option.value as Date
              );
            } else {
              formattedObject[option.field] = option.value;
            }
            return formattedObject;
          }, {} as Record<string, any>);

        setExpandedFilters(
          Object.keys(formattedFilters).length === 0 ? null : formattedFilters
        );
      } else {
        // Handle plain object (saved filters)
        setExpandedFilters(
          Object.values(filtersToSendToServer).every((value) => value === null)
            ? null
            : filtersToSendToServer
        );
      }
    },
    [handlePageChange, setExpandedFilters]
  );

  return (
    <ReportsLayout>
      <div className="reportInProgressBox">
        <Title
          {...params.title}
          checkedReportsIds={checkedReportsIds}
          bodyToExportExcel={{
            expandedReportsFilters: expandedFilters,
            reportsFilters: simpleFilters,
            searchInput: searchKeyword,
          }}
          onReportRefetch={onReportRefetch}
        />
        <div className={renderClassName()}>
          {renderStatusesFilter()}
          {renderRestOfFilters()}
          {renderFilterButton()}
          <MultiFilterContainer
            isFollowUp={isFollowUp}
            role={role}
            isOpenMultiSelect={isOpenMultiSelect}
            isOpenSavedFilter={isOpenSavedFilter}
            setIsOpenMultiSelect={setIsOpenMultiSelect}
            setIsOpenSavedFilter={setIsOpenSavedFilter}
            params={params}
            toggleOpenMultiSelect={toggleOpenMultiSelect}
            toggleOpenSavedFilter={toggleOpenSavedFilter}
            filterType={FilterType.reportType}
            submitFilter={submitExpandedFilter}
          />
        </div>
        <div className={renderClassName()}>
          {isFollowUp && role === UserRole.CONTROLCENTER ? (
            <RangeDatePicker {...params.rangeDatePicker} />
          ) : null}
        </div>

        {renderList()}
        {!isLoading && totalPages > 1 && (
          <div className="pagination">
            <CustomPagination
              totalPages={totalPages}
              activePage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </ReportsLayout>
  );
};

export default ReportsBase;
